import { ReactNode } from 'react';
import { MdArrowRightAlt, MdOutlineDirectionsCar } from 'react-icons/md';
import { TmdxAvatar, TmdxChip } from '@ds';
import { getFullName, gramsToKg, mmToCm } from '@/utils/helpers';
import { CaseLog, CaseLogEventType, CaseLogRecord, CaseNote, CaseResourceType, LogResource } from '@/api/apis/case';
import { SYSTEM_USER_ID } from '@/constants/user.constants';
import { EmployeeRole } from '@/api/apis/resources';
import { DONOR_DATA_LOG_RECORDS_PROPERTY_ORDER, LOG_RECORDS_PROPERTY_ORDER } from './constants';

export const renderFromTo = (prev: ReactNode, current: ReactNode) => (
  <div className="flex flex-1 flex-wrap gap-2 truncate">
    {prev}
    <MdArrowRightAlt size={24} className="flex-none" />
    {current}
  </div>
);

export const renderTextIcon = (text: string, icon?: ReactNode) => (
  <div className="grid grid-flow-col auto-cols-auto items-center justify-start gap-1 truncate">
    {icon ?? <MdOutlineDirectionsCar size={20} />}
    <span className="text-m-m truncate">{text}</span>
  </div>
);

// TODO: Add profiles render logic after API ready
export const renderResource = (resource?: Partial<LogResource> | string, chip?: ReactNode, role?: ReactNode) => {
  if (!resource || typeof resource === 'string') return <TmdxAvatar size="md" />;

  const { profilePhoto, firstName, lastName, resourceName, serialNumber, tailNumber, resourceType } = resource;
  let name;
  if (resourceType === CaseResourceType.Aircraft) name = tailNumber || serialNumber;
  else if (resourceType === CaseResourceType.OCSConsole) name = resourceName;
  else {
    name = getFullName(firstName, lastName);
  }

  return (
    <>
      {role}
      <div className="flex items-center gap-1">
        <TmdxAvatar size="md" radius="md" name={name} src={profilePhoto} />
        <span className="text-s-m text-base-5 truncate">{name}</span>
      </div>
      {chip}
    </>
  );
};

const parseValue = (propertyName: string, value?: string) => {
  try {
    const facilityFields = ['DonorHospitalDetails', 'TxCenterDetails'];
    const isFacility = facilityFields.includes(propertyName);
    if (!value) return '';
    return isFacility ? JSON.parse(value)?.name : value;
  } catch {
    return '';
  }
};

const classNames = { content: 'truncate', base: 'min-w-0! w-full! flex flex-1 truncate' };

export const renderDetailsRecordField = (record: CaseLogRecord) => {
  const prevValue = parseValue(record.propertyName, record.previousValue);
  const nextValue = parseValue(record.propertyName, record.value);

  if (!record.propertyName) return null;

  return (
    <>
      <span className="text-s-m leading-none">{record.propertyName}: </span>
      {prevValue ? (
        renderFromTo(
          <TmdxChip classNames={classNames} size="sm">
            <div className="flex-1 truncate">{prevValue}</div>
          </TmdxChip>,
          <TmdxChip classNames={classNames} size="sm" color="info">
            <div className="flex-1 truncate">{nextValue || 'None'}</div>
          </TmdxChip>,
        )
      ) : (
        <TmdxChip classNames={classNames} size="sm">
          <div className="flex-1 truncate">{nextValue}</div>
        </TmdxChip>
      )}
    </>
  );
};

const getFormattedDonorDataValue = <T extends (...args: string[]) => string>(
  value: string | undefined,
  formatter: T,
  unit: 'cm' | 'kg' = 'cm',
): string => {
  const formattedValue = value ? formatter(value) : 'N/A';

  return formattedValue === 'N/A' ? formattedValue : `${formattedValue} ${unit}`;
};

export const renderDonorDataUpdatedRecordField = (record: CaseLogRecord) => {
  switch (record.propertyName) {
    case 'DonorDemographics.Height':
      return renderDetailsRecordField({
        ...record,
        previousValue: getFormattedDonorDataValue(
          record.previousValue,
          (value: string) => String(mmToCm(Number(value), 2)),
          'cm',
        ),
        value: getFormattedDonorDataValue(record.value, (value: string) => String(mmToCm(Number(value), 2)), 'cm'),
      });
    case 'DonorDemographics.Weight':
      return renderDetailsRecordField({
        ...record,
        previousValue: getFormattedDonorDataValue(
          record.previousValue,
          (value: string) => String(gramsToKg(Number(value), 3)),
          'kg',
        ),
        value: getFormattedDonorDataValue(record.value, (value: string) => String(gramsToKg(Number(value), 3)), 'kg'),
      });
    default:
      return renderDetailsRecordField(record);
  }
};

export const getNoteByCreatedAt = (notes: CaseNote[], log: CaseLog) => {
  if (!Array.isArray(notes) || notes.length === 0) {
    return null;
  }

  const matchingNote = notes.find(
    (note) => new Date(note.modifiedAt).toISOString() === new Date(log.createdAt).toISOString(),
  );

  if (!matchingNote) {
    return null;
  }

  return matchingNote;
};

export const getCaseLogUserName = (user: CaseLog['user'], userId?: string) => {
  if (!user) return { fullName: '', userName: '' };

  if (userId === SYSTEM_USER_ID) return { fullName: 'System user', userName: 'System user' };

  const fullName = getFullName(user.firstName, user.lastName);

  return {
    fullName,
    userName: user.roles?.includes(EmployeeRole.TechSupportAdmin) ? `Tech Support user ${fullName}` : fullName,
  };
};

const sortLogs = (logs: CaseLog[] | undefined) => (logs?.length ? [...logs].reverse() : []);

const LOG_RECORD_ORDER_BY_EVENT_TYPE: Partial<Record<CaseLogEventType, Record<string, number>>> = {
  [CaseLogEventType.CaseDetailsUpdated]: LOG_RECORDS_PROPERTY_ORDER,
  [CaseLogEventType.CaseRecipientHospitalORDataUpdated]: LOG_RECORDS_PROPERTY_ORDER,
  [CaseLogEventType.DonorDataUpdated]: DONOR_DATA_LOG_RECORDS_PROPERTY_ORDER,
};

const sortLogRecordsByWeight = (logRecords: CaseLogRecord[], eventType: CaseLogEventType): CaseLogRecord[] => {
  const propertyOrder = LOG_RECORD_ORDER_BY_EVENT_TYPE[eventType];
  if (logRecords.length <= 1 || !propertyOrder) {
    return logRecords;
  }

  return [...logRecords].sort((a, b) => {
    const weightA = propertyOrder[a.propertyName] ?? Number.MAX_SAFE_INTEGER;
    const weightB = propertyOrder[b.propertyName] ?? Number.MAX_SAFE_INTEGER;
    return weightA - weightB;
  });
};

// An empty "Details"/"Link" field (e.g. CtScan set to Yes but no link entered) has neither a
// previous nor a next value, so it renders as a blank chip. Drop it instead of showing nothing.
const isEmptyDonorDataRecord = (record: CaseLogRecord) => !record.previousValue && !record.value;

export const prepareLogs = (data: CaseLog[] | undefined) => {
  return sortLogs(data).map((log) => ({
    ...log,
    logRecords: sortLogRecordsByWeight(
      log.eventType === CaseLogEventType.DonorDataUpdated
        ? log.logRecords.filter((record) => !isEmptyDonorDataRecord(record))
        : log.logRecords,
      log.eventType,
    ),
  }));
};
